import { useEffect, useState } from 'react';
import { getStrataManagerSuitesByProperty } from 'src/services/client';
import { IStrataManagerPropertyGroup } from 'src/pages/StrataManagerSuites/types';

export interface UseStrataManagerSuitesDataReturn {
  propertyGroups: IStrataManagerPropertyGroup[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

// apiReq resolves to the raw Response on success (or an {isError} object on failure).
const unwrapGroups = async (
  response: any
): Promise<IStrataManagerPropertyGroup[]> => {
  if (response?.isError || !response?.ok) {
    throw new Error(response?.error ?? response?.message ?? 'Request failed');
  }

  const json: any = await response.json();

  // Assumes the endpoint returns groups (property → units). If it returns a FLAT
  // unit list, group by property here instead — confirm the shape with the API.
  return json?.data ?? json;
};

export const useStrataManagerSuitesData =
  (): UseStrataManagerSuitesDataReturn => {
    const [propertyGroups, setPropertyGroups] = useState<
      IStrataManagerPropertyGroup[]
    >([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    const fetchData = async (): Promise<void> => {
      setIsLoading(true);
      setError(null);

      try {
        const response: any = await getStrataManagerSuitesByProperty();

        const groups: IStrataManagerPropertyGroup[] =
          await unwrapGroups(response);

        setPropertyGroups(groups);
      } catch (err) {
        console.error('Failed to load Strata Manager units:', err);
        setError('Could not load your units. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    useEffect(() => {
      void fetchData();
    }, []);

    return {
      propertyGroups,
      isLoading,
      error,
      refetch: fetchData,
    };
  };